import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Target, Users, Star, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const YouthSports = () => {
  const sports = [
    {
      name: 'Basketball',
      description: 'Develop fundamental skills, teamwork, and competitive spirit through structured basketball training and league play.',
      icon: Trophy,
      link: null
    },
    {
      name: 'Track & Field',
      description: 'Build speed, endurance, and discipline through comprehensive track programs and competitive meets.',
      icon: Target,
      link: 'https://www.missingelementsports.com/copy-of-summer-training-offers'
    },
    {
      name: '7on7 Football',
      description: 'Learn the fundamentals of football while developing character, teamwork, and athletic ability.',
      icon: Users,
      link: 'https://www.missingelementsports.com/about-1-1'
    }
  ];

  const benefits = [
    'Professional coaching and mentorship',
    'Character and leadership development',
    'Team building and collaboration skills',
    'Physical fitness and healthy habits',
    'Competitive opportunities and exposure',
    'College recruitment preparation'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-6 px-6 py-2 bg-[#E6B933]/20 rounded-full border border-[#E6B933]">
                <p className="text-[#E6B933] font-semibold">Athletic Development</p>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Youth <span className="text-[#E6B933]">Sports</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Building champions on and off the field through comprehensive sports programs that develop athletic skills, character, and leadership.
              </p>
              <Link to="/get-involved">
                <Button className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                  Join Our Programs
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#E6B933]/20 rounded-2xl blur-xl"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_why-me-org/artifacts/sysufm8j_ME%2012u.png" 
                alt="Youth Sports Team" 
                className="relative rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sports Programs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-[#E6B933]">Sports Programs</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive athletic development across multiple sports
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {sports.map((sport, index) => {
              const IconComponent = sport.icon;
              const cardContent = (
                <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#E6B933] group h-full">
                  <div className="w-16 h-16 bg-[#E6B933]/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#E6B933] transition-colors duration-300">
                    <IconComponent className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{sport.name}</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{sport.description}</p>
                  {sport.link && (
                    <span className="inline-flex items-center text-[#E6B933] font-semibold mt-auto">
                      Learn More <ArrowRight className="ml-2" size={18} />
                    </span>
                  )}
                </Card>
              );
              
              return sport.link ? (
                <a key={index} href={sport.link} target="_blank" rel="noopener noreferrer" className="block">
                  {cardContent}
                </a>
              ) : (
                cardContent
              );
            })}
          </div>
        </div>
      </section>

      {/* Program Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                More Than <span className="text-[#E6B933]">Just Sports</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our youth sports programs go beyond athletic training. We focus on developing the whole person—building character, leadership skills, and life lessons that extend far beyond the playing field.
              </p>
              <div className="grid grid-cols-1 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#E6B933] flex items-center justify-center flex-shrink-0">
                      <Star className="text-white" size={14} />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_why-me-org/artifacts/ru0xvpf8_ME%20TRACK%201.png" 
                alt="Track athletes" 
                className="rounded-lg shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_why-me-org/artifacts/mdkmyo0b_RUNWME.jpg" 
                alt="Running program" 
                className="rounded-lg shadow-lg w-full h-64 object-cover mt-8 hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Missing Element Sports Link */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-8 md:p-12 bg-gradient-to-br from-gray-50 to-white border-2 border-[#E6B933]">
            <div className="text-center">
              <Trophy className="w-16 h-16 text-[#E6B933] mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Missing Element <span className="text-[#E6B933]">Sports Performance</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                For advanced athletic training and sports performance programs, visit our partner organization Missing Element Sports Performance.
              </p>
              <a href="https://missingelementsports.com" target="_blank" rel="noopener noreferrer">
                <Button className="bg-black hover:bg-gray-800 text-white font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                  Visit Missing Element Sports
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </a>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">
            Ready to <span className="text-[#E6B933]">Get Started?</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join our youth sports programs and start your journey toward athletic and personal excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-involved">
              <Button className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                Sign Up Today
              </Button>
            </Link>
            <a href="mailto:thewhymefoundation@gmail.com?subject=Youth Sports Inquiry">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-6 text-lg transition-all duration-300">
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YouthSports;
